import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import { createApp, type App } from 'vue';
import HomeV2WelcomeDialog from '../components/HomeV2WelcomeDialog.vue';
import type {
    HomeV2WelcomeCloseReason,
    HomeV2WelcomeHandle,
    HomeV2WelcomePresenter,
    HomeV2WelcomeShowOptions,
} from '../home-v2-welcome-presenter';

/** Welcome 原生弹窗退场动画时长。 */
const WELCOME_EXIT_DURATION_MS = 320;

/** 将 Home V2 Welcome 弹窗独立挂载到 document.body。 */
@injectable()
export class HomeV2WelcomePresenterImpl implements HomeV2WelcomePresenter {
    /** 挂载单次 Welcome 弹窗并返回幂等生命周期句柄。 */
    // eslint-disable-next-line max-lines-per-function
    show(options: HomeV2WelcomeShowOptions): HomeV2WelcomeHandle {
        const host = document.createElement('div');
        host.dataset.testid = 'home-v2-welcome-host';
        host.className = 'home-v2-welcome-host';
        let app: App<Element> | undefined;
        let isSettled = false;
        let isDisposed = false;
        let closeReason: HomeV2WelcomeCloseReason | undefined;
        let exitTimerId: number | undefined;
        let resolveResult!: (reason: HomeV2WelcomeCloseReason) => void;
        const previousBodyOverflow = document.body.style.overflow;
        const result = new Promise<HomeV2WelcomeCloseReason>((resolve) => {
            resolveResult = resolve;
        });

        const handleKeydown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') close('dismiss');
        };
        const cleanup = () => {
            if (isDisposed) return;
            isDisposed = true;
            if (exitTimerId !== undefined) window.clearTimeout(exitTimerId);
            document.removeEventListener('keydown', handleKeydown);
            app?.unmount();
            host.remove();
            document.body.style.overflow = previousBodyOverflow;
        };
        const finish = (reason: HomeV2WelcomeCloseReason) => {
            if (isSettled) return;
            isSettled = true;
            cleanup();
            resolveResult(reason);
        };
        const close = (reason: HomeV2WelcomeCloseReason) => {
            if (isSettled || closeReason) return;
            closeReason = reason;
            host.classList.add('home-v2-welcome-host--closing');
            document.removeEventListener('keydown', handleKeydown);
            const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
            exitTimerId = window.setTimeout(
                () => finish(reason),
                prefersReducedMotion ? 0 : WELCOME_EXIT_DURATION_MS,
            );
        };
        const dispose = () => {
            finish(closeReason ?? 'dismiss');
        };

        try {
            document.body.appendChild(host);
            app = createApp(HomeV2WelcomeDialog, {
                ...options,
                onCta: () => close('cta'),
                onDismiss: () => close('dismiss'),
            });
            app.mount(host);
            document.body.style.overflow = 'hidden';
            document.addEventListener('keydown', handleKeydown);
        } catch (error) {
            cleanup();
            throw error;
        }

        return { result, close, dispose };
    }
}
